/**
 * Types for Dashboard Personalization
 */

/** User roles that determine dashboard layout */
export type UserRole =
  | 'developer'
  | 'devops-engineer'
  | 'platform-engineer'
  | 'tech-lead'
  | 'architect'
  | 'data-engineer'
  | 'sre'
  | 'manager'
  | 'other';

/** Interest areas for recommendations */
export type UserInterest =
  | 'apis'
  | 'microservices'
  | 'cloud-infrastructure'
  | 'data-engineering'
  | 'machine-learning'
  | 'security'
  | 'ci-cd'
  | 'documentation'
  | 'innersource'
  | 'testing'
  | 'observability'
  | 'kubernetes'
  | 'serverless';

/** Dashboard widget preferences */
export interface DashboardPreferences {
  showRecommendations: boolean;
  itemsPerWidget: number;
}

/** Complete user profile for personalization */
export interface UserProfile {
  version: number;
  createdAt: string;
  updatedAt: string;
  setupCompleted: boolean;
  role: UserRole | null;
  squad: string | null;
  divisions: string[];
  platforms: string[];
  interests: UserInterest[];
  dashboardPreferences: DashboardPreferences;
  /** Preferred catalog entity tabs (empty = show all) */
  preferredTabs?: string[];
}

/** Role option for display */
export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

/** Interest option for display */
export interface InterestOption {
  value: UserInterest;
  label: string;
  category:
    | 'development'
    | 'infrastructure'
    | 'data'
    | 'operations'
    | 'quality';
}

/** Default dashboard preferences */
export const DEFAULT_DASHBOARD_PREFERENCES: DashboardPreferences = {
  showRecommendations: true,
  itemsPerWidget: 5,
};

/** Default user profile */
export const DEFAULT_USER_PROFILE: UserProfile = {
  version: 1,
  createdAt: '',
  updatedAt: '',
  setupCompleted: false,
  role: null,
  squad: null,
  divisions: [],
  platforms: [],
  interests: [],
  dashboardPreferences: DEFAULT_DASHBOARD_PREFERENCES,
};

/** Available user roles */
export const USER_ROLES: RoleOption[] = [
  {
    value: 'developer',
    label: 'Developer',
    description: 'Building applications and services',
  },
  {
    value: 'devops-engineer',
    label: 'DevOps Engineer',
    description: 'CI/CD pipelines and automation',
  },
  {
    value: 'platform-engineer',
    label: 'Platform Engineer',
    description: 'Building developer platforms',
  },
  {
    value: 'tech-lead',
    label: 'Tech Lead',
    description: 'Leading technical teams',
  },
  {
    value: 'architect',
    label: 'Architect',
    description: 'System design and strategy',
  },
  {
    value: 'data-engineer',
    label: 'Data Engineer',
    description: 'Data pipelines and analytics',
  },
  {
    value: 'sre',
    label: 'SRE',
    description: 'Site reliability and operations',
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Team and project management',
  },
  { value: 'other', label: 'Other', description: 'Other role' },
];

/** Available interest areas */
export const USER_INTERESTS: InterestOption[] = [
  { value: 'apis', label: 'APIs', category: 'development' },
  { value: 'microservices', label: 'Microservices', category: 'development' },
  { value: 'ci-cd', label: 'CI/CD', category: 'operations' },
  {
    value: 'cloud-infrastructure',
    label: 'Cloud Infrastructure',
    category: 'infrastructure',
  },
  { value: 'kubernetes', label: 'Kubernetes', category: 'infrastructure' },
  { value: 'serverless', label: 'Serverless', category: 'infrastructure' },
  { value: 'data-engineering', label: 'Data Engineering', category: 'data' },
  { value: 'machine-learning', label: 'Machine Learning', category: 'data' },
  { value: 'security', label: 'Security', category: 'quality' },
  { value: 'testing', label: 'Testing', category: 'quality' },
  { value: 'observability', label: 'Observability', category: 'operations' },
  { value: 'documentation', label: 'Documentation', category: 'development' },
  { value: 'innersource', label: 'InnerSource', category: 'development' },
];
